import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Chip,
  Alert,
  IconButton,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardHeader,
  CardContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DeviceScriptResponse } from './api/scriptService';
import scriptService from './api/scriptService';

// Create a custom type that extends DeviceScriptResponse to include error cases
type ExtendedDeviceScriptResponse = DeviceScriptResponse & {
  error?: string;
};

interface ScriptsPanelProps {
  selectedDevices: string[];
  open: boolean;
  onClose: () => void;
}

// Convert color names to hex values
const colorNameToHex: Record<string, string> = {
  orange: '#FF9800',
  red: '#F44336',
  blue: '#2196F3',
  green: '#4CAF50',
  purple: '#9C27B0',
  yellow: '#FFEB3B',
  black: '#000000',
  white: '#FFFFFF',
  gray: '#9E9E9E',
};

// Function to get a valid color value for MUI
const getValidColor = (color: string): string => {
  // If it's already a hex, rgb, or hsl value, return it
  if (color.startsWith('#') || color.startsWith('rgb') || color.startsWith('hsl')) {
    return color;
  }
  
  // Convert color name to lowercase and look up in our dictionary
  const lowerColor = color.toLowerCase();
  return colorNameToHex[lowerColor] || '#9E9E9E'; // Default to gray if not found
};

const ScriptsPanel: React.FC<ScriptsPanelProps> = ({ selectedDevices, open, onClose }) => {
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ExtendedDeviceScriptResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const runReadScript = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await scriptService.runMacioReadScript(selectedDevices);
      setResults(response as ExtendedDeviceScriptResponse[]);
    } catch (err) {
      setError('Failed to run script. Please try again.');
      console.error('Script execution error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'success': return theme.palette.success.main;
      case 'warning': return theme.palette.warning.main;
      case 'error': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: '90vh',
          width: '95vw',
          maxWidth: '95vw',
          bgcolor: theme.palette.background.default,
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        pb: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}>
        <Typography variant="h6">Scripts Panel</Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, bgcolor: theme.palette.background.default }}>
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: theme.palette.background.paper,
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Selected Devices: <strong>{selectedDevices.join(', ')}</strong>
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={runReadScript}
            disabled={loading || selectedDevices.length === 0}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            size="small"
          >
            Read Device Data
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {results.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {results.map((deviceData) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={deviceData.box_id}>
                  <DeviceCard deviceData={deviceData} getSeverityColor={getSeverityColor} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface DeviceCardProps {
  deviceData: ExtendedDeviceScriptResponse;
  getSeverityColor: (severity: string) => string;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ deviceData, getSeverityColor }) => {
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';
  
  // Check if device has error
  if ('error' in deviceData && deviceData.error) {
    return (
      <Card variant="outlined" sx={{ 
        height: '100%',
        border: `1px solid ${alpha(theme.palette.error.main, 0.5)}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        borderRadius: '6px',
        overflow: 'hidden',
      }}>
        <CardHeader 
          title={
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>
              Box {deviceData.box_id}
            </Typography>
          }
          sx={{ 
            pb: 0,
            bgcolor: isDarkTheme ? 'rgba(58, 26, 26, 0.7)' : alpha(theme.palette.error.light, 0.2),
            borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}
        />
        
        <CardContent sx={{ 
          bgcolor: isDarkTheme ? theme.palette.background.paper : '#fff',
          pt: 2 
        }}>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 1,
              bgcolor: isDarkTheme ? 'rgba(58, 26, 26, 0.9)' : undefined,
              color: isDarkTheme ? '#ffcccc' : undefined,
              '& .MuiAlert-icon': {
                color: isDarkTheme ? '#ff6b6b' : undefined
              }
            }}
          >
            {String(deviceData.error)}
          </Alert>
        </CardContent>
      </Card>
    );
  }
  
  // Get a valid color for the provider
  const providerColor = deviceData.system && deviceData.system.sim_provider 
    ? getValidColor(deviceData.system.sim_provider.color)
    : theme.palette.grey[500];
  
  return (
    <Card variant="outlined" sx={{ 
      height: '100%',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      borderRadius: '6px',
      overflow: 'hidden',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        borderColor: alpha(theme.palette.primary.main, 0.3),
      }
    }}>
      <CardHeader 
        title={
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            Box {deviceData.box_id}
          </Typography>
        }
        subheader={
          deviceData.system && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              {deviceData.system.modem && (
                <Chip 
                  label={deviceData.system.modem} 
                  size="small" 
                  sx={{ 
                    fontSize: '0.7rem',
                    bgcolor: isDarkTheme ? alpha(theme.palette.primary.main, 0.15) : alpha('#e0e0e0', 0.7),
                  }}
                />
              )}
              {deviceData.system.sim_provider && (
                <Chip 
                  label={deviceData.system.sim_provider.value} 
                  size="small"
                  sx={{ 
                    bgcolor: alpha(providerColor, isDarkTheme ? 0.2 : 0.1),
                    color: providerColor,
                    fontSize: '0.7rem'
                  }} 
                />
              )}
            </Box>
          )
        }
        sx={{ 
          pb: 0,
          bgcolor: isDarkTheme ? alpha(theme.palette.primary.dark, 0.15) : alpha(theme.palette.primary.light, 0.05),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      />
      
      <CardContent sx={{ 
        pt: 1, 
        pb: 1,
        bgcolor: isDarkTheme ? theme.palette.background.paper : '#fff',
      }}>
        {deviceData.system && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.8rem', fontWeight: 'bold' }}>
              System Info
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, fontSize: '0.75rem' }}>
              {deviceData.system.ram && <InfoChip label="RAM" value={deviceData.system.ram} />}
              {deviceData.system.flash && <InfoChip label="Flash" value={deviceData.system.flash} />}
              {deviceData.system.version && (
                <InfoChip 
                  label="Version" 
                  value={`IM${deviceData.system.version.im}/SW${deviceData.system.version.sw}`} 
                />
              )}
            </Box>
          </Box>
        )}

        {deviceData.measurements && (
          <>
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.8rem', fontWeight: 'bold' }}>
                Measurements
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ 
                mb: 1,
                border: `1px solid ${theme.palette.divider}`,
                '& .MuiTable-root': {
                  bgcolor: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : alpha('#fafafa', 0.7),
                }
              }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
                  <TableBody>
                    {Object.entries(deviceData.measurements)
                      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                      .map(([key, item]) => (
                        <TableRow key={key} 
                          sx={{
                            '&:nth-of-type(odd)': {
                              bgcolor: isDarkTheme ? alpha(theme.palette.action.hover, 0.05) : alpha('#f5f5f5', 0.3),
                            },
                            '&:hover': { 
                              bgcolor: isDarkTheme ? alpha(theme.palette.action.hover, 0.1) : alpha(theme.palette.action.hover, 0.05),
                            }
                          }}
                        >
                          <TableCell component="th" scope="row" sx={{ fontSize: '0.75rem' }}>
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'medium' }}>
                            {item.value.toFixed(1)} {key.includes('temp') ? '°C' : 
                                     key.includes('humid') ? '%' : 
                                     key.includes('press') ? 'hPa' : ''}
                          </TableCell>
                          <TableCell align="right" sx={{ width: '40px' }}>
                            <StatusDot 
                              status={item.valid ? 'valid' : 'invalid'} 
                              color={getSeverityColor(item.severity)} 
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}

        {deviceData.status && (
          <>
            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.8rem', fontWeight: 'bold' }}>
                Status
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Object.entries(deviceData.status)
                  .filter(([key]) => 
                    // Show only important status items to save space
                    ['box', 'vpn', 'network', 'hardware', 'sensors', 'cron', 'brightness'].includes(key)
                  )
                  .map(([key, item]) => {
                    const statusValue = typeof item.status === 'boolean' 
                      ? (item.status ? 'OK' : 'Failed') 
                      : (item.status || 'N/A');
                    
                    return (
                      <Chip 
                        key={key}
                        label={`${key}: ${statusValue}`} 
                        size="small"
                        sx={{ 
                          fontSize: '0.65rem',
                          height: 20,
                          bgcolor: alpha(getSeverityColor(item.severity || 'default'), isDarkTheme ? 0.2 : 0.1),
                          color: isDarkTheme ? alpha(getSeverityColor(item.severity || 'default'), 0.9) : getSeverityColor(item.severity || 'default'),
                          border: isDarkTheme ? `1px solid ${alpha(getSeverityColor(item.severity || 'default'), 0.3)}` : 'none',
                        }}
                      />
                    );
                  })}
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const InfoChip = ({ label, value }: { label: string; value: string }) => {
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';
  
  return (
    <Chip 
      label={`${label}: ${value}`} 
      size="small"
      sx={{ 
        fontSize: '0.65rem',
        height: 20,
        bgcolor: isDarkTheme ? alpha(theme.palette.primary.main, 0.15) : alpha('#e0e0e0', 0.7),
        color: isDarkTheme ? theme.palette.primary.light : theme.palette.text.primary,
      }}
    />
  );
};

const StatusDot = ({ status, color }: { status: string, color: string }) => (
  <Box 
    sx={{ 
      width: 8, 
      height: 8, 
      borderRadius: '50%', 
      backgroundColor: color,
      display: 'inline-block'
    }}
    title={status}
  />
);

export default ScriptsPanel; 